import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  SITE_URL,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  findPublicRoute,
  isPrivatePath,
} from "../seo/metadata";

/**
 * Keeps the document head correct across client-side navigation.
 *
 * The head a crawler reads is written server-side by services/api/seo/inject.js
 * — this is what fixes the tab title, the canonical and the share card once
 * React takes over routing, since the SPA never reloads the document.
 *
 * Mounted once in App rather than per page, and it resolves its own copy from
 * the same route table the server reads. Per-page <Seo> tags would mean the
 * title lived in two places and drifted the first time anyone edited one.
 * Props exist only for pages whose metadata can't be known statically.
 *
 * Tags are upserted by selector rather than appended, so navigating replaces
 * the previous page's metadata instead of stacking a second <title> or a
 * canonical that contradicts the injected one.
 */

const upsertMeta = (attr, key, content) => {
  const existing = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) {
    // Removing beats leaving the old value: a stale og:url carried onto a
    // private gallery would still be advertising the page we just left.
    if (existing) existing.remove();
    return;
  }
  const el = existing || document.createElement("meta");
  if (!existing) {
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, href) => {
  const existing = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (existing) existing.remove();
    return;
  }
  const el = existing || document.createElement("link");
  if (!existing) {
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/** "/" stays "/", "/terms/" becomes "/terms" — one canonical form per page. */
const canonicalUrl = (routePath) => {
  const cleaned =
    routePath.length > 1 ? routePath.replace(/\/+$/, "") || "/" : "/";
  return `${SITE_URL}${cleaned}`;
};

const Seo = ({ title, description, image, noindex }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const route = findPublicRoute(pathname);

    // Anything not in the public table is unindexable — private token pages,
    // the authenticated app, and unknown paths the catch-all answers with a
    // soft 200. A caller can force noindex but can never force the reverse.
    const blocked = noindex || !route || isPrivatePath(pathname);

    const pageTitle = title || (route && route.title) || DEFAULT_TITLE;
    const pageDescription =
      description || (route && route.description) || DEFAULT_DESCRIPTION;
    const ogImage = image || DEFAULT_OG_IMAGE;

    document.title = pageTitle;
    upsertMeta("name", "description", pageDescription);
    upsertMeta("property", "og:title", pageTitle);
    upsertMeta("property", "og:description", pageDescription);
    upsertMeta("name", "twitter:title", pageTitle);
    upsertMeta("name", "twitter:description", pageDescription);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("name", "twitter:image", ogImage);
    upsertMeta("name", "robots", blocked ? "noindex, nofollow" : "index, follow");

    // Blocked pages get no canonical and no og:url. Either would republish the
    // share token in the markup we are trying to keep out of an index.
    const url = blocked ? null : canonicalUrl(pathname);
    upsertLink("canonical", url);
    upsertMeta("property", "og:url", url);
  }, [pathname, title, description, image, noindex]);

  return null;
};

export default Seo;
